import { tryCatch } from '@/try-catch';
import { ANALYSIS_TERMINAL_STATUSES, AnalysisStatus } from '@/features/workspaces/constants';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { api } from '@/api';
import {
  LevelingFrame,
  type LevelingRunStatusResponse,
  type ProcurementComparison,
  type ProcurementPackage,
  type ProcurementTemplate,
  type SubcontractorOffer,
  type SubRfp,
  type SuggestDisciplinesResponse,
  type WorkPackageAssessment,
  type WorkPackageFindings,
  type WorkPackageItems,
  type WorkPackageNarratives,
  type LegacyScopeItem,
} from '@/features/subcontractor-bids/types';
import { scopeItemRow } from '@/features/subcontractor-bids/utils';
import type { ScopeRow } from '@/features/scope-breakdown/types';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { parseStreamRow, queryStream } from '@/features/workspaces/stream';

export const procurementsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ['procurements', workspaceId],
    queryFn: async () =>
      (
        await api.get<ProcurementPackage[]>('v1/work-packages', {
          params: { tenderId: workspaceId },
        })
      ).data,
  });

const PROCUREMENT_DETAILS_QUERY_KEY = 'procurementDetails';
const PROCUREMENT_COMPARISON_QUERY_KEY = 'procurementComparison';
const PROCUREMENT_ITEMS_QUERY_KEY = 'procurementItems';
const PROCUREMENT_ASSESSMENT_QUERY_KEY = 'procurementAssessment';
const PROCUREMENT_FINDINGS_QUERY_KEY = 'procurementFindings';

export const procurementDetailsQuery = (workspaceId: string, procurementId: string) =>
  queryOptions({
    queryKey: [PROCUREMENT_DETAILS_QUERY_KEY, workspaceId, procurementId],
    queryFn: async () =>
      (await api.get<ProcurementPackage>(`v1/work-packages/${procurementId}`)).data,
  });

export const suggestedDisciplinesQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ['suggestedDisciplines', workspaceId],
    queryFn: async () =>
      (
        await api.get<SuggestDisciplinesResponse>('v1/work-packages/suggest-disciplines', {
          params: { tenderId: workspaceId },
        })
      ).data,
  });

export const invalidateScopeConsumers = (queryClient: QueryClient, workspaceId: string) => {
  const packIds = new Set(
    (
      queryClient.getQueryData<ProcurementPackage[]>(procurementsQuery(workspaceId).queryKey) ?? []
    ).map((pack) => pack.id),
  );
  for (const [key] of queryClient.getQueriesData({
    queryKey: [PROCUREMENT_DETAILS_QUERY_KEY, workspaceId],
  }))
    if (typeof key[2] === 'string') packIds.add(key[2]);
  return Promise.all([
    queryClient.invalidateQueries(suggestedDisciplinesQuery(workspaceId)),
    queryClient.invalidateQueries(procurementsQuery(workspaceId)),
    queryClient.invalidateQueries({ queryKey: [PROCUREMENT_DETAILS_QUERY_KEY, workspaceId] }),
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        [
          PROCUREMENT_ITEMS_QUERY_KEY,
          PROCUREMENT_COMPARISON_QUERY_KEY,
          PROCUREMENT_ASSESSMENT_QUERY_KEY,
          PROCUREMENT_FINDINGS_QUERY_KEY,
          NARRATIVES_QUERY_KEY,
        ].includes(String(queryKey[0])) && packIds.has(String(queryKey[1])),
    }),
  ]);
};

export const procurementTemplatesQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ['procurementTemplates', workspaceId],
    queryFn: async () =>
      (
        await api.get<ProcurementTemplate[]>('v1/work-packages/templates', {
          params: { tenderId: workspaceId },
        })
      ).data,
  });

export const NARRATIVES_QUERY_KEY = 'narratives' as const;

export const narrativesQuery = (procurementId: string) =>
  queryOptions({
    queryKey: [NARRATIVES_QUERY_KEY, procurementId],
    queryFn: async () =>
      (await api.get<WorkPackageNarratives>(`v1/work-packages/${procurementId}/narratives`)).data,
    refetchInterval: (query) =>
      query.state.data?.columns.some((c) => c.status === AnalysisStatus.IN_PROGRESS) ? 8000 : false,
  });

export const offersQuery = (workspaceId: string, workPackageId: string) =>
  queryOptions({
    queryKey: ['offers', workspaceId, workPackageId],
    queryFn: async () =>
      (
        await api.get<SubcontractorOffer[]>('v1/subcontractors', {
          params: { tenderId: workspaceId, workPackageId },
        })
      ).data,
  });

export const levelingRunQuery = (resourceId: string) => {
  const queryKey = ['levelingRun', resourceId];
  const invalidate = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey });
  const refreshCompletedResults = (queryClient: QueryClient, next: LevelingRunStatusResponse) => {
    const current = queryClient.getQueryData<LevelingRunStatusResponse>(queryKey);
    if (current?.status !== AnalysisStatus.IN_PROGRESS || next.status !== AnalysisStatus.DONE)
      return;
    queryClient.invalidateQueries({ queryKey: [PROCUREMENT_COMPARISON_QUERY_KEY, resourceId] });
    queryClient.invalidateQueries(procurementAssessmentQuery(resourceId));
    queryClient.invalidateQueries(procurementFindingsQuery(resourceId));
  };
  return queryOptions({
    queryKey,
    queryFn: async ({ client }) => {
      const { data } = await api.get<LevelingRunStatusResponse>(
        `v1/work-packages/${resourceId}/leveling-run`,
        { timeout: 15000 },
      );
      refreshCompletedResults(client, data);
      return data;
    },
    meta: {
      stream: queryStream<
        LevelingRunStatusResponse,
        | {
            rows: {
              run_id: string | null;
              status: number | null;
              progress: number;
              frame: LevelingFrame;
              created_at: string | null;
              finished_at: string | null;
            }[];
          }
        | { id?: string | null; status: AnalysisStatus | null; progress: number | null }
      >({
        topic: 'leveling_run',
        type: DOCUMENT_TYPE.workPackage,
        resourceId,
        isActive: (data) => data?.status === AnalysisStatus.IN_PROGRESS,
        onStatusUpdate: (payload, queryClient) => {
          if ('rows' in payload) {
            if (!Array.isArray(payload.rows)) return invalidate(queryClient);
            const row = payload.rows.find((item) => item.frame === 'tender');
            if (!row) return invalidate(queryClient);

            const next = {
              runId: row.run_id,
              status: row.status,
              progress: row.progress,
              createdAt: row.created_at,
              finishedAt: row.finished_at,
            };
            refreshCompletedResults(queryClient, next);
            return queryClient.setQueryData(queryKey, next);
          }

          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate(queryClient);
          if (!row) return;
          if (ANALYSIS_TERMINAL_STATUSES.includes(row.status)) return invalidate(queryClient);

          const current = queryClient.getQueryData<LevelingRunStatusResponse>(queryKey);
          if (!current || current.runId !== row.rowId) return invalidate(queryClient);

          queryClient.setQueryData<LevelingRunStatusResponse>(queryKey, {
            ...current,
            status: row.status,
            progress: row.progress,
          });
        },
      }),
    },
  });
};

export const subRfpQuery = (procurementId: string) =>
  queryOptions({
    queryKey: ['subRfp', procurementId],
    queryFn: async () => {
      const { data, error } = await tryCatch(
        api.get<SubRfp>(`v1/work-packages/${procurementId}/sub-rfp`),
      );
      if (isAxiosError(error) && error.response?.status === 404) return null;
      if (error) throw error;
      return data.data;
    },
    refetchInterval: (query) =>
      query.state.data?.status === AnalysisStatus.IN_PROGRESS ? 8000 : false,
  });

export const procurementComparisonQuery = (procurementId: string, frame: LevelingFrame) =>
  queryOptions({
    queryKey: [PROCUREMENT_COMPARISON_QUERY_KEY, procurementId, frame],
    queryFn: async (): Promise<ProcurementComparison> => {
      const { data } = await api.get<
        Omit<ProcurementComparison, 'scopeRequiredRows'> & {
          scopeRequiredRows: (ProcurementComparison['scopeRequiredRows'][number] & {
            scopeStatement?: string | null;
            chapterPath?: string[];
            statementSources?: {
              chunkId: string;
              docId: string | null;
              documentFilename: string | null;
              page: number | null;
              sourceSpan: string | null;
            }[];
          })[];
        }
      >(`v1/work-packages/${procurementId}/comparison`, { params: { frame } });
      return {
        ...data,
        scopeRequiredRows: data.scopeRequiredRows.map((row) => ({
          ...row,
          description: row.scopeStatement ?? row.description,
          section: row.section ?? row.chapterPath?.join(' › ') ?? null,
          sources: row.statementSources?.length
            ? row.statementSources.flatMap((source) =>
                source.docId == null ? [] : [{ ...source, docId: source.docId, section: null }],
              )
            : (row.sources ?? (row.source ? [row.source] : [])),
        })),
      };
    },
  });

export const procurementItemsQuery = (procurementId: string) =>
  queryOptions({
    queryKey: [PROCUREMENT_ITEMS_QUERY_KEY, procurementId],
    queryFn: async (): Promise<WorkPackageItems> => {
      const { data } = await api.get<
        Omit<WorkPackageItems, 'scopeItems'> & {
          scopeItems: (ScopeRow | LegacyScopeItem)[];
        }
      >(`v1/work-packages/${procurementId}/items`);
      return { ...data, scopeItems: data.scopeItems.map(scopeItemRow) };
    },
  });

export const offerDetailsQuery = (subcontractorId: string) =>
  queryOptions({
    queryKey: ['offerDetails', subcontractorId],
    queryFn: async () =>
      (await api.get<SubcontractorOffer>(`v1/subcontractors/${subcontractorId}`)).data,
  });

const isAnalysing = (assessment: WorkPackageAssessment | undefined) =>
  !!assessment &&
  (assessment.leveling?.status === AnalysisStatus.IN_PROGRESS ||
    assessment.scorecards.some((s) => s.analyses.some((a) => a.status === 'IN_PROGRESS')));

export const procurementAssessmentQuery = (procurementId: string) =>
  queryOptions({
    queryKey: [PROCUREMENT_ASSESSMENT_QUERY_KEY, procurementId],
    queryFn: async ({ client, queryKey }) => {
      const wasAnalysing = isAnalysing(client.getQueryData<WorkPackageAssessment>(queryKey));
      const { data } = await api.get<WorkPackageAssessment>(
        `v1/work-packages/${procurementId}/assessment`,
      );
      if (wasAnalysing && !isAnalysing(data))
        client.invalidateQueries(procurementFindingsQuery(procurementId));
      return data;
    },
    refetchInterval: (query) =>
      query.state.data?.scorecards.some((s) => s.analyses.some((a) => a.status === 'IN_PROGRESS'))
        ? 8000
        : false,
  });

export const procurementFindingsQuery = (procurementId: string) =>
  queryOptions({
    queryKey: [PROCUREMENT_FINDINGS_QUERY_KEY, procurementId],
    queryFn: async () =>
      (await api.get<WorkPackageFindings>(`v1/work-packages/${procurementId}/findings`)).data,
  });
